import {
  envelope,
  isEnvelope,
  type ControlAcknowledgement,
  type ControlEvent,
  type PlaybackTelemetry,
  type SessionCreateRequest,
  type SessionCreateResponse,
  type SessionEnvelope,
  type SessionSnapshot,
  type TimelineUpdate
} from "@stimulantnoise/protocol";
import { pendingControls, removePendingControl } from "./storage";

export type SessionClientEvent =
  | { type: "connection"; state: "connecting" | "connected" | "reconnecting" | "offline" }
  | { type: "snapshot"; value: SessionSnapshot }
  | { type: "timeline"; value: TimelineUpdate }
  | { type: "ack"; value: ControlAcknowledgement }
  | { type: "degraded"; reason: string }
  | { type: "error"; message: string };

export async function createSession(request: SessionCreateRequest): Promise<SessionCreateResponse> {
  const response = await fetch("/v1/sessions", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request)
  });
  if (!response.ok) throw new Error(`session creation failed (${response.status})`);
  return response.json() as Promise<SessionCreateResponse>;
}

export class SessionClient {
  readonly #listeners = new Set<(event: SessionClientEvent) => void>();
  #socket: WebSocket | undefined;
  #stopped = false;
  #attempt = 0;
  #lastRevision = -1;
  #playback: () => Promise<PlaybackTelemetry>;

  constructor(
    private readonly sessionId: string,
    private readonly token: string,
    playback: () => Promise<PlaybackTelemetry>
  ) {
    this.#playback = playback;
  }

  subscribe(listener: (event: SessionClientEvent) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(event: SessionClientEvent): void {
    for (const listener of this.#listeners) listener(event);
  }

  connect(): void {
    this.#stopped = false;
    this.#open();
  }

  #open(): void {
    this.#emit({ type: "connection", state: this.#attempt ? "reconnecting" : "connecting" });
    const url = new URL(`/v1/sessions/${this.sessionId}/events`, location.href);
    url.protocol = location.protocol === "https:" ? "wss:" : "ws:";
    url.searchParams.set("token", this.token);
    const socket = new WebSocket(url);
    this.#socket = socket;
    socket.onopen = () => void this.#onOpen();
    socket.onmessage = (message) => this.#onMessage(message.data);
    socket.onerror = () => this.#emit({ type: "error", message: "realtime connection error" });
    socket.onclose = () => this.#reconnect();
  }

  async #onOpen(): Promise<void> {
    this.#attempt = 0;
    this.#emit({ type: "connection", state: "connected" });
    this.sendEnvelope("session.resume", {
      last_timeline_revision: this.#lastRevision,
      playback: await this.#playback()
    });
    for (const event of await pendingControls()) this.sendEnvelope("control.submit", event);
  }

  #onMessage(data: unknown): void {
    if (typeof data !== "string") return;
    let value: unknown;
    try { value = JSON.parse(data); } catch { return; }
    if (!isEnvelope(value)) return;
    const message = value as SessionEnvelope;
    if (message.session_id !== this.sessionId) return;
    switch (message.type) {
      case "session.snapshot": {
        const snapshot = message.payload as SessionSnapshot;
        this.#lastRevision = snapshot.timeline_revision;
        this.#emit({ type: "snapshot", value: snapshot });
        break;
      }
      case "timeline.update": {
        const update = message.payload as TimelineUpdate;
        if (update.timeline_revision > this.#lastRevision) this.#lastRevision = update.timeline_revision;
        this.#emit({ type: "timeline", value: update });
        break;
      }
      case "control.ack": {
        const ack = message.payload as ControlAcknowledgement;
        void removePendingControl(ack.event_id);
        this.#emit({ type: "ack", value: ack });
        break;
      }
      case "session.degraded":
        this.#emit({ type: "degraded", reason: String((message.payload as { reason?: string }).reason ?? "degraded") });
        break;
      case "error":
        this.#emit({ type: "error", message: String((message.payload as { message?: string }).message ?? "session error") });
        break;
    }
  }

  #reconnect(): void {
    this.#socket = undefined;
    if (this.#stopped) return;
    this.#emit({ type: "connection", state: navigator.onLine ? "reconnecting" : "offline" });
    const delay = Math.min(15_000, 500 * 2 ** Math.min(this.#attempt++, 5)) * (0.75 + Math.random() * 0.5);
    window.setTimeout(() => {
      if (!this.#stopped) this.#open();
    }, delay);
  }

  sendControl(event: ControlEvent): boolean {
    return this.sendEnvelope("control.submit", event);
  }

  sendTelemetry(playback: PlaybackTelemetry): boolean {
    return this.sendEnvelope("playback.telemetry", playback);
  }

  private sendEnvelope(type: SessionEnvelope["type"], payload: unknown): boolean {
    if (this.#socket?.readyState !== WebSocket.OPEN) return false;
    this.#socket.send(JSON.stringify(envelope(this.sessionId, type, payload)));
    return true;
  }

  stop(): void {
    this.#stopped = true;
    this.#socket?.close(1000, "client stopped");
    this.#socket = undefined;
    this.#emit({ type: "connection", state: "offline" });
  }
}
